<template>
  <div class="mini-kasko-calc">
<!--    <Breadcrumbs :breadcrumbs="breadcrumbs" />-->

    <section class="mini-kasko-calc__card">
      <h1 class="mini-kasko-calc__title">Міні Каско — розрахунок</h1>

      <div class="mini-kasko-calc__field">
        <label class="mini-kasko-calc__label" for="plate-input">Держномер авто</label>
        <div class="mini-kasko-calc__plate-row">
          <input
            id="plate-input"
            v-model="plate"
            class="mini-kasko-calc__input"
            :class="{ 'mini-kasko-calc__input--invalid': plateError }"
            type="text"
            placeholder="AA0000AA"
            maxlength="8"
            @input="onPlateInput"
            @keydown.enter="lookupCar"
          />
          <button
            type="button"
            class="mini-kasko-calc__lookup-btn"
            :disabled="!plate || lookupLoading"
            @click="lookupCar"
          >
            {{ lookupLoading ? 'Пошук...' : 'Знайти' }}
          </button>
        </div>
        <small v-if="plateError" class="mini-kasko-calc__error">{{ plateError }}</small>
      </div>

      <div class="mini-kasko-calc__field">
        <div class="mini-kasko-calc__city-header">
          <label class="mini-kasko-calc__label">Місто реєстрації</label>
          <button
            v-if="!manualCity"
            type="button"
            class="mini-kasko-calc__mode-link"
            @click="switchToManual"
          >
            Вказати вручну
          </button>
          <button
            v-else-if="autoCity"
            type="button"
            class="mini-kasko-calc__mode-link"
            @click="switchToAuto"
          >
            Повернути автоматично
          </button>
        </div>

        <div v-if="!manualCity && autoCity" class="mini-kasko-calc__city-auto">
          <span class="mini-kasko-calc__city-name">{{ autoCity.name_full_name_ua }}</span>
        </div>

        <div v-else-if="!manualCity && !autoCity" class="mini-kasko-calc__city-hint">
          Введіть держномер і натисніть «Знайти» для автоматичного визначення
        </div>

        <MiniKaskoCityField
          v-if="manualCity"
          v-model="manualCityId"
          :selected-label="manualCityLabel"
          :error="cityError"
          @change="onCityChange"
        />
        <small v-if="cityError && !manualCity" class="mini-kasko-calc__error">{{ cityError }}</small>
      </div>

      <div class="mini-kasko-calc__actions">
        <button
          type="button"
          class="mini-kasko-calc__submit-btn"
          :disabled="!canCalculate || calculating"
          @click="calculate"
        >
          {{ calculating ? 'Завантаження...' : 'Розрахувати' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import MiniKaskoCityField from './components/MiniKaskoCityField.vue';

export default {
  name: 'MiniKaskoCalculator',

  components: { Breadcrumbs, MiniKaskoCityField },

  data() {
    return {
      plate: '',
      plateError: '',
      lookupLoading: false,

      autoCity: null,

      manualCity: false,
      manualCityId: null,
      manualCityLabel: '',
      cityError: '',

      calculating: false,
    };
  },

  computed: {
    breadcrumbs() {
      return [
        { label: 'Головна', to: { name: 'home' } },
        { label: 'Міні Каско' },
      ];
    },

    resolvedCityId() {
      if (this.manualCity) {
        return this.manualCityId || null;
      }
      return this.autoCity?.id || null;
    },

    resolvedCityName() {
      if (this.manualCity) {
        return this.manualCityLabel || '';
      }
      return this.autoCity?.name_full_name_ua || this.autoCity?.name_ua || this.autoCity?.name || '';
    },

    canCalculate() {
      return Boolean(this.resolvedCityId);
    },
  },

  methods: {
    onPlateInput() {
      this.plateError = '';
      this.autoCity = null;
      if (!this.manualCity) {
        this.cityError = '';
      }
    },

    async lookupCar() {
      const plate = this.plate.trim();

      if (!plate) {
        this.plateError = 'Введіть держномер';
        return;
      }

      this.lookupLoading = true;
      this.plateError = '';
      this.autoCity = null;

      try {
        const { data } = await axios.get(`/api/directories/car/${plate}`);
        const city = data?.data?.city ?? null;

        if (city) {
          this.autoCity = city;
          this.manualCity = false;
        } else {
          this.manualCity = true;
          this.cityError = 'Місто не знайдено — оберіть вручну';
        }
      } catch {
        this.manualCity = true;
        this.cityError = 'Не вдалося визначити місто — оберіть вручну';
      } finally {
        this.lookupLoading = false;
      }
    },

    switchToManual() {
      this.manualCity = true;
      this.cityError = '';
    },

    switchToAuto() {
      this.manualCity = false;
      this.manualCityId = null;
      this.manualCityLabel = '';
      this.cityError = '';
    },

    onCityChange(option) {
      this.manualCityLabel = option?.name_full_name_ua || option?.name || '';
      this.cityError = '';
    },

    calculate() {
      if (!this.resolvedCityId) {
        this.cityError = 'Оберіть місто реєстрації';
        return;
      }

      this.$router.push({
        name: 'miniKaskoOffers',
        query: {
          city_id: this.resolvedCityId,
          city_name: this.resolvedCityName || undefined,
          plate: this.plate.trim() || undefined,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-calc {
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px;

  &__title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 24px;
    color: #09090b;
  }

  &__card {
    background: #fff;
    border: 1px solid #e4e4e7;
    border-radius: 12px;
    padding: 24px;
  }

  &__field {
    margin-bottom: 20px;
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #3f3f46;
    margin-bottom: 6px;
  }

  &__plate-row {
    display: flex;
    gap: 8px;
  }

  &__input {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font: inherit;
    font-size: 14px;
    color: #09090b;
    outline: none;
    text-transform: uppercase;
    transition: border-color 0.2s;

    &:focus {
      border-color: #fb923c;
      box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
    }

    &--invalid {
      border-color: #ef4444;
    }

    &::placeholder {
      color: #a1a1aa;
    }
  }

  &__lookup-btn {
    flex-shrink: 0;
    padding: 0 16px;
    min-height: 44px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f4f4f5;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s, border-color 0.2s;

    &:hover:not(:disabled) {
      background: #e4e4e7;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__city-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  &__city-header &__label {
    margin-bottom: 0;
  }

  &__mode-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 13px;
    color: #2563eb;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: #1d4ed8;
    }
  }

  &__city-auto {
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    font-size: 14px;
    color: #09090b;
    display: flex;
    align-items: center;
  }

  &__city-hint {
    min-height: 44px;
    padding: 10px 12px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    font-size: 13px;
    color: #71717a;
    display: flex;
    align-items: center;
  }

  &__error {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #dc2626;
  }

  &__actions {
    margin-top: 8px;
  }

  &__submit-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    background: #fb923c;
    border: none;
    border-radius: 8px;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    transition: background 0.2s;
    text-align: center;

    &:hover:not(:disabled) {
      background: #f97316;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
